/**
 * Arrow Curve Builder - Convert motion state to CurveMath-compatible CurveProps
 *
 * Bridges the gap between:
 * - Motion arrows (dynamic MotionValues, simplified calculations)
 * - Static arrows (CurveMath.calculatePoints with Point objects)
 *
 * This allows motion arrows to use the same sophisticated curve calculation
 * logic as static arrows, ensuring visual consistency.
 */

import type { CurveProps, Point } from '@alchemiesolns/kaff-arrow/src/types';
import type { FormatOptions } from '@alchemiesolns/svg-format';
import type { ArrowData } from '../rendering/useSVGGeneration';

/**
 * Build CurveProps for PA arrow (Lone Pair → Atom)
 *
 * @param sourceX - Source atom X position
 * @param sourceY - Source atom Y position
 * @param destX - Destination atom X position
 * @param destY - Destination atom Y position
 * @param lpDirections - Array of normalized LP direction vectors [[x, y, z], ...]. Can be single or multiple directions.
 * @param arrowData - Arrow metadata (destOffset, etc.)
 * @param format - Visual format constants
 * @returns CurveProps ready for CurveMath.calculatePoints()
 */
export function buildCurvePropsPA(
  sourceX: number,
  sourceY: number,
  destX: number,
  destY: number,
  lpDirections: number[][] | number[],
  arrowData: ArrowData,
  format: FormatOptions
): CurveProps {
  // Handle both single direction [x, y, z] and array of directions [[x, y, z], ...]
  let directions: number[][];
  if (Array.isArray(lpDirections[0])) {
    // Multiple directions [[x, y, z], ...]
    directions = (lpDirections as number[][]).map(dir => [dir[0], dir[1], dir[2] ?? 0]);
  } else {
    // Single direction [x, y, z]
    const singleDir = lpDirections as number[];
    directions = [[singleDir[0], singleDir[1], singleDir[2] ?? 0]];
  }

  // Source point: lone pair with direction(s)
  const sourcePoint: Point = {
    position: [sourceX, sourceY, 0],
    type: 'lonepair',
    direction: directions,
    offset: format.lonePair.offset,
  };

  // Destination point: atom (particle type)
  const destPoint: Point = {
    position: [destX, destY, 0],
    type: 'particle',
    direction: [[0, 0, 0]],
    offset: arrowData.destOffset,
  };

  return {
    source: sourcePoint,
    destination: destPoint,
    cameraDir: [0, 0, 1],
    isDestAdjacent: false, // LP → Atom is never adjacent
    type: 'curve',
    format,
  };
}

/**
 * Build CurveProps for PB arrow (Lone Pair → Bond)
 *
 * @param sourceX - Source atom X position
 * @param sourceY - Source atom Y position
 * @param bondMidX - Bond midpoint X position
 * @param bondMidY - Bond midpoint Y position
 * @param bondAtom1X - Bond atom1 X position
 * @param bondAtom1Y - Bond atom1 Y position
 * @param bondAtom2X - Bond atom2 X position
 * @param bondAtom2Y - Bond atom2 Y position
 * @param lpDirections - Array of normalized LP direction vectors [[x, y, z], ...]. Can be single or multiple directions.
 * @param arrowData - Arrow metadata
 * @param format - Visual format constants
 * @returns CurveProps ready for CurveMath.calculatePoints()
 */
export function buildCurvePropsPB(
  sourceX: number,
  sourceY: number,
  bondMidX: number,
  bondMidY: number,
  bondAtom1X: number,
  bondAtom1Y: number,
  bondAtom2X: number,
  bondAtom2Y: number,
  lpDirections: number[][] | number[],
  arrowData: ArrowData,
  format: FormatOptions
): CurveProps {
  // Handle both single direction [x, y, z] and array of directions [[x, y, z], ...]
  let directions: number[][];
  if (Array.isArray(lpDirections[0])) {
    // Multiple directions [[x, y, z], ...]
    directions = (lpDirections as number[][]).map(dir => [dir[0], dir[1], dir[2] ?? 0]);
  } else {
    // Single direction [x, y, z]
    const singleDir = lpDirections as number[];
    directions = [[singleDir[0], singleDir[1], singleDir[2] ?? 0]];
  }

  // Source point: lone pair with direction(s)
  const sourcePoint: Point = {
    position: [sourceX, sourceY, 0],
    type: 'lonepair',
    direction: directions,
    offset: format.lonePair.offset,
  };

  // Calculate bond direction (normalized)
  const bondDx = bondAtom2X - bondAtom1X;
  const bondDy = bondAtom2Y - bondAtom1Y;
  const bondLength = Math.sqrt(bondDx * bondDx + bondDy * bondDy);
  const bondDir = bondLength > 0 ? [bondDx / bondLength, bondDy / bondLength, 0] : [1, 0, 0];

  // Destination point: bond with direction
  const destPoint: Point = {
    position: [bondMidX, bondMidY, 0],
    type: 'bond',
    direction: [bondDir],
    offset: arrowData.destOffset,
  };

  return {
    source: sourcePoint,
    destination: destPoint,
    cameraDir: [0, 0, 1],
    isDestAdjacent: false, // LP → Bond is never adjacent
    type: 'curve',
    format,
  };
}

/**
 * Build CurveProps for BA arrow (Bond → Atom)
 *
 * @param bondMidX - Source bond midpoint X
 * @param bondMidY - Source bond midpoint Y
 * @param bondAtom1X - Bond atom1 X position
 * @param bondAtom1Y - Bond atom1 Y position
 * @param bondAtom2X - Bond atom2 X position
 * @param bondAtom2Y - Bond atom2 Y position
 * @param destX - Destination atom X
 * @param destY - Destination atom Y
 * @param arrowData - Arrow metadata
 * @param format - Visual format constants
 * @returns CurveProps ready for CurveMath.calculatePoints()
 */
export function buildCurvePropsBA(
  bondMidX: number,
  bondMidY: number,
  bondAtom1X: number,
  bondAtom1Y: number,
  bondAtom2X: number,
  bondAtom2Y: number,
  destX: number,
  destY: number,
  arrowData: ArrowData,
  format: FormatOptions
): CurveProps {
  // Calculate bond direction (normalized)
  const bondDx = bondAtom2X - bondAtom1X;
  const bondDy = bondAtom2Y - bondAtom1Y;
  const bondLength = Math.sqrt(bondDx * bondDx + bondDy * bondDy);
  const bondDir = bondLength > 0 ? [bondDx / bondLength, bondDy / bondLength, 0] : [1, 0, 0];

  // Source point: bond with direction
  const sourcePoint: Point = {
    position: [bondMidX, bondMidY, 0],
    type: 'bond',
    direction: [bondDir],
    offset: 0, // Bonds don't have offset from their midpoint
  };

  // Destination point: atom
  const destPoint: Point = {
    position: [destX, destY, 0],
    type: 'particle',
    direction: [[0, 0, 0]],
    offset: arrowData.destOffset,
  };

  // Check if destination is adjacent to source bond
  const isDestAdjacent =
    (arrowData.destAtomId === arrowData.sourceBondAtomIds?.[0]) ||
    (arrowData.destAtomId === arrowData.sourceBondAtomIds?.[1]);

  return {
    source: sourcePoint,
    destination: destPoint,
    cameraDir: [0, 0, 1],
    isDestAdjacent,
    type: 'curve',
    format,
  };
}

/**
 * Build CurveProps for BB arrow (Bond → Bond)
 *
 * @param sourceBondMidX - Source bond midpoint X
 * @param sourceBondMidY - Source bond midpoint Y
 * @param sourceBondAtom1X - Source bond atom1 X
 * @param sourceBondAtom1Y - Source bond atom1 Y
 * @param sourceBondAtom2X - Source bond atom2 X
 * @param sourceBondAtom2Y - Source bond atom2 Y
 * @param destBondMidX - Dest bond midpoint X
 * @param destBondMidY - Dest bond midpoint Y
 * @param destBondAtom1X - Dest bond atom1 X
 * @param destBondAtom1Y - Dest bond atom1 Y
 * @param destBondAtom2X - Dest bond atom2 X
 * @param destBondAtom2Y - Dest bond atom2 Y
 * @param arrowData - Arrow metadata
 * @param format - Visual format constants
 * @returns CurveProps ready for CurveMath.calculatePoints()
 */
export function buildCurvePropsBB(
  sourceBondMidX: number,
  sourceBondMidY: number,
  sourceBondAtom1X: number,
  sourceBondAtom1Y: number,
  sourceBondAtom2X: number,
  sourceBondAtom2Y: number,
  destBondMidX: number,
  destBondMidY: number,
  destBondAtom1X: number,
  destBondAtom1Y: number,
  destBondAtom2X: number,
  destBondAtom2Y: number,
  arrowData: ArrowData,
  format: FormatOptions
): CurveProps {
  // Calculate source bond direction (normalized)
  const srcBondDx = sourceBondAtom2X - sourceBondAtom1X;
  const srcBondDy = sourceBondAtom2Y - sourceBondAtom1Y;
  const srcBondLength = Math.sqrt(srcBondDx * srcBondDx + srcBondDy * srcBondDy);
  const srcBondDir = srcBondLength > 0 ? [srcBondDx / srcBondLength, srcBondDy / srcBondLength, 0] : [1, 0, 0];

  // Calculate dest bond direction (normalized)
  const destBondDx = destBondAtom2X - destBondAtom1X;
  const destBondDy = destBondAtom2Y - destBondAtom1Y;
  const destBondLength = Math.sqrt(destBondDx * destBondDx + destBondDy * destBondDy);
  const destBondDir = destBondLength > 0 ? [destBondDx / destBondLength, destBondDy / destBondLength, 0] : [1, 0, 0];

  // Source point: bond with direction
  const sourcePoint: Point = {
    position: [sourceBondMidX, sourceBondMidY, 0],
    type: 'bond',
    direction: [srcBondDir],
    offset: 0,
  };

  // Destination point: bond with direction
  const destPoint: Point = {
    position: [destBondMidX, destBondMidY, 0],
    type: 'bond',
    direction: [destBondDir],
    offset: arrowData.destOffset,
  };

  // Check if bonds are adjacent (share a common atom)
  const sourceBondAtoms = arrowData.sourceBondAtomIds;
  const destBondAtoms = arrowData.destBondAtomIds;
  const isDestAdjacent = sourceBondAtoms && destBondAtoms
    ? sourceBondAtoms.some((atom) => destBondAtoms.includes(atom))
    : false;

  return {
    source: sourcePoint,
    destination: destPoint,
    cameraDir: [0, 0, 1],
    isDestAdjacent,
    type: 'curve',
    format,
  };
}
